import React, {
  useState,
  createContext,
  useContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  ApolloLink,
} from "@apollo/client";
import { LOCAL_STORAGES } from "utils";
// import { LOCAL_STORAGES } from "@/utils";
// import { router } from "expo-router";
// import { clearStorage } from "@/state";

interface ApolloAuthProviderProps {
  login: (token: any, userId: string) => Promise<void>;
  logout: () => Promise<void>;
  userId?: string;
  accessToken?: string;
  loaded?: boolean
}
const AuthContext = createContext<ApolloAuthProviderProps | undefined>(
  undefined
);

// Custom hook to use the AuthContext
export const useApolloAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("parameters not set");
  return ctx;
};

const ApolloAuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    (async () => {
      const accessToken = await localStorage.getItem(
        LOCAL_STORAGES.ACCESS_TOKEN
      );
      const userId = await localStorage.getItem(
        LOCAL_STORAGES.USER_ID
      );
      setAccessToken(accessToken??undefined);
      setUserId(userId??undefined);
      setLoaded(true)
    })();
  }, []);

  // Function to update the token (e.g., after logging in)
  const login:ApolloAuthProviderProps["login"] = async (token, userId) => {
    await localStorage.setItem(LOCAL_STORAGES.ACCESS_TOKEN, token);
    await localStorage.setItem(LOCAL_STORAGES.USER_ID, userId);
    setAccessToken(token);
    setUserId(userId);
    setLoaded(true)
  }
  const logout:ApolloAuthProviderProps["logout"] = async () => {
    await localStorage.removeItem(LOCAL_STORAGES.ACCESS_TOKEN);
    await localStorage.removeItem(LOCAL_STORAGES.USER_ID);
    setAccessToken(undefined)
    setUserId(undefined)
    setLoaded(false)
    // clearStorage()
    // router.replace("/(auth)")
  }


  // Auth link to add the accessToken in the headers if it exists
  const authLink =  useMemo(() => new ApolloLink((operation, forward) => {
    const prevContext = operation.getContext();
    const prevHeaders = prevContext.headers || {};
    const headers: Record<string, string> = {
      ...prevHeaders, 
      "user-x-id": userId || "",
    };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    operation.setContext({
      headers,
    });
    return forward(operation);
  }), [accessToken])

  const uri = `${process.env.REACT_APP_BACKEND_URL}/graphql`
  // Create the Apollo Client with authLink
  const client = new ApolloClient({
    link: ApolloLink.from([
      authLink,
      new HttpLink({ uri }),
    ]),
    cache: new InMemoryCache(),
  });

  return (
    <AuthContext.Provider value={{ login, logout, userId, accessToken, loaded }}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </AuthContext.Provider>
  );
};

export default ApolloAuthProvider;
